import type { McqOtherOptionConfig } from '../../fetchSurvey/types/mcq';
import { parseMcqOtherCountryValue, parseMcqOtherDateValue } from './mcqOtherUtils';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function countDecimalPlaces(value: string): number {
  const dotIndex = value.indexOf('.');
  if (dotIndex === -1) return 0;
  return value.length - dotIndex - 1;
}

function validateText(config: Extract<McqOtherOptionConfig, { contentType: 'TEXT' }>, text: string): boolean {
  const length = text.length;
  if (config.minCharacters !== undefined && length < config.minCharacters) {
    return false;
  }
  if (config.maxCharacters !== undefined && length > config.maxCharacters) {
    return false;
  }
  return true;
}

function validateNumeric(
  config: Extract<McqOtherOptionConfig, { contentType: 'NUMERIC' }>,
  text: string
): boolean {
  const trimmed = text.trim();
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    return false;
  }

  const value = Number(trimmed);
  if (!Number.isFinite(value)) {
    return false;
  }

  if (config.minNumber !== undefined && value < config.minNumber) {
    return false;
  }
  if (config.maxNumber !== undefined && value > config.maxNumber) {
    return false;
  }
  if (config.decimalCount !== undefined && countDecimalPlaces(trimmed) > config.decimalCount) {
    return false;
  }

  return true;
}

function validatePhoneOrPostal(
  config: Extract<McqOtherOptionConfig, { contentType: 'PHONE_NUMBER' | 'POSTAL_CODE' }>,
  text: string
): boolean {
  const parsed = parseMcqOtherCountryValue(text);
  if (!parsed) {
    return false;
  }

  const normalizedCountryCode = parsed.countryCode.toUpperCase();
  if (
    !config.countryCodes.some(code => code.toUpperCase() === normalizedCountryCode)
  ) {
    return false;
  }

  const local = parsed.value.trim();
  if (!local) {
    return false;
  }

  if (config.contentType === 'PHONE_NUMBER') {
    return /^\d{4,15}$/.test(local.replace(/[\s()-]/g, ''));
  }

  return /^[A-Za-z0-9][A-Za-z0-9\s-]{1,11}$/.test(local);
}

function validateDate(
  config: Extract<McqOtherOptionConfig, { contentType: 'DATE' }>,
  text: string
): boolean {
  const date = parseMcqOtherDateValue(text, config.dateFormat);
  if (!date) {
    return false;
  }

  const timestamp = date.getTime();
  if (config.minDate !== undefined && timestamp < config.minDate) {
    return false;
  }
  if (config.maxDate !== undefined && timestamp > config.maxDate) {
    return false;
  }

  return true;
}

export function isMcqOtherTextValid(config: McqOtherOptionConfig, text: string): boolean {
  const trimmed = text.trim();
  if (!trimmed) {
    return false;
  }

  switch (config.contentType) {
    case 'TEXT':
      return validateText(config, trimmed);
    case 'NUMERIC':
      return validateNumeric(config, trimmed);
    case 'EMAIL':
      return EMAIL_PATTERN.test(trimmed);
    case 'PHONE_NUMBER':
    case 'POSTAL_CODE':
      return validatePhoneOrPostal(config, trimmed);
    case 'DATE':
      return validateDate(config, trimmed);
    default:
      return true;
  }
}
